import {
  useEffect, useState, type ReactNode,
} from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Modell } from '../api';
import type { Rahmen } from './Projektrahmen';
import { PageHeader } from '../rahmen/PageHeader';
import { ErrorState, LoadingState } from '../muster';
import { Einstufungsregeln } from './koordination/Einstufungsregeln';
import { Gewerkevorrang } from './koordination/Gewerkevorrang';
import { Gleichrangregeln } from './koordination/Gleichrangregeln';
import { Hoehenbaender } from './koordination/Hoehenbaender';
import { Loesungsarten } from './koordination/Loesungsarten';
import { Toleranzen } from './koordination/Toleranzen';
import { KiAnbieter } from './koordination/KiAnbieter';
import { Planungsabschnitte } from './koordination/Planungsabschnitte';
import { Pflichtbauteile } from './koordination/Pflichtbauteile';
import { KollisionenUndEinstufung } from './koordination/KollisionenUndEinstufung';
import { Auswertung } from './koordination/Auswertung';
import { Planeraufgaben } from './koordination/Planeraufgaben';
import { AufnahmeUndStand } from './koordination/AufnahmeUndStand';

/**
 * N4 (F69) — dreizehn der zwanzig internen Karten von `Koordination.tsx`
 * als echte Unterrouten, nach demselben Muster wie `SanitaerSeiten.tsx`
 * (S1): ein duenner Wrapper je Karte, der seine eigenen Daten holt und die
 * bereits vorhandene, rein anzeigende Komponente aus `seiten/koordination/`
 * damit fuellt. `Koordination.tsx` selbst bleibt UNVERAENDERT — die vier
 * Reiter (`?reiter=`) funktionieren weiter wie bisher; diese Seiten sind
 * ein ZWEITER, zusaetzlicher Weg zum selben Inhalt, kein Ersatz.
 *
 * Sieben der 20 Karten (Baum, Kollisionsliste, Vorschaubild, Arbeitsvorrat,
 * Bericht, Loesungen, Grundlagen) bekommen hier BEWUSST keine eigene Route
 * — Begruendung im Kopfkommentar von `navigation/struktur.ts`
 * (`koordinationEintraege`) und im Bericht von N4.
 *
 * N6 hat diese sieben erneut geprueft (der Umbau, den N4 ausschloss): der
 * geteilte Zustand zwischen Baum und Kollisionsliste (`baumGewaehlt`,
 * `baumArt`) ist jetzt in `Koordination.tsx` selbst aus `useState` in die
 * URL (`?knoten=`, `?baumart=`) gehoben - eine eigene Route bekommt davon
 * KEINE der sieben Karten, Begruendung im N6-Nachtrag in `abschnitte.ts`.
 */

function useKoordinationGrundlagen(api: ApiClient, projektId: string) {
  const [d, setD] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    setD(null);
    setFehler(null);

    api.koordinationGrundlagen(projektId)
      .then(setD)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  return { d, fehler };
}

/** Gemeinsamer Rahmen: Titel, Lade-/Fehlerzustand, dann der eigentliche Inhalt. */
function GrundlagenKarte({
  titel, beschreibung, children,
}: {
  titel: string;
  beschreibung?: string;
  children: (d: any) => ReactNode;
}) {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { d, fehler } = useKoordinationGrundlagen(api, projekt.id);

  return (
    <>
      <PageHeader titel={titel} beschreibung={beschreibung} />
      {fehler && (
        <ErrorState
          meldung="Die Koordinationsgrundlagen konnten nicht geladen werden."
          technisch={fehler}
        />
      )}
      {!d && !fehler && (
        <LoadingState text="Grundlagen werden geladen …" zeilen={6} spalten={4} />
      )}
      {d && children(d)}
    </>
  );
}

// ─── Regeln ─────────────────────────────────────────────────────────────
// Die sieben Regelseiten der Koordination (Plan Abschnitt 7/8, `art: 'regeln'`).

export function KoordinationEinstufungsregelnSeite() {
  return (
    <GrundlagenKarte
      titel="Koordination — Einstufungsregeln"
      beschreibung="Die Rangfolge, nach der eine Durchdringung als echt, vorgesehen oder irrelevant eingestuft wird."
    >
      {(d) => <Einstufungsregeln regeln={d.regeln ?? []} />}
    </GrundlagenKarte>
  );
}

export function KoordinationGewerkevorrangSeite() {
  return (
    <GrundlagenKarte
      titel="Koordination — Gewerkevorrang"
      beschreibung="Wer weicht wem — die Rangfolge ZWISCHEN Gewerken."
    >
      {(d) => <Gewerkevorrang vorrang={d.vorrang ?? []} />}
    </GrundlagenKarte>
  );
}

export function KoordinationGleichrangregelnSeite() {
  return (
    <GrundlagenKarte
      titel="Koordination — Gleichrangregeln"
      beschreibung="Wo der Gewerkevorrang nichts zu sagen hat, weil beide Seiten demselben Gewerk angehören."
    >
      {(d) => <Gleichrangregeln regeln={d.gleichrangregeln ?? []} />}
    </GrundlagenKarte>
  );
}

export function KoordinationHoehenbaenderSeite() {
  return (
    <GrundlagenKarte
      titel="Koordination — Höhenbänder"
      beschreibung="Der freie Raum je Trasse, aus dem die Ausweichvorschläge der Stufe 2 gerechnet werden."
    >
      {(d) => <Hoehenbaender hoehenbaender={d.hoehenbaender ?? []} />}
    </GrundlagenKarte>
  );
}

export function KoordinationLoesungsartenSeite() {
  return (
    <GrundlagenKarte
      titel="Koordination — Lösungsarten"
      beschreibung="Die Kategorien, in die eine Anweisung fällt."
    >
      {(d) => <Loesungsarten loesungsarten={d.loesungsarten ?? []} />}
    </GrundlagenKarte>
  );
}

export function KoordinationToleranzenSeite() {
  return (
    <GrundlagenKarte
      titel="Koordination — Toleranzen"
      beschreibung="Montagezuschlag und Mindestabstände, gegen die gerechnet wird."
    >
      {(d) => <Toleranzen einstellungen={d.einstellungen ?? []} />}
    </GrundlagenKarte>
  );
}

export function KoordinationKiAnbieterSeite() {
  return (
    <GrundlagenKarte
      titel="Koordination — KI-Anbieter"
      beschreibung="Der Anbieter, der die KI-Bewertung einzelner Kollisionen liefert."
    >
      {(d) => <KiAnbieter ki={d.ki} />}
    </GrundlagenKarte>
  );
}

// ─── Planung ────────────────────────────────────────────────────────────

export function KoordinationPlanungsabschnitteSeite() {
  return (
    <GrundlagenKarte
      titel="Koordination — Planungsabschnitte"
      beschreibung="Die Abschnitte, in die das Bauwerk für die Koordination zerlegt ist."
    >
      {(d) => <Planungsabschnitte bereiche={d.bereiche ?? []} />}
    </GrundlagenKarte>
  );
}

export function KoordinationPflichtbauteileSeite() {
  return (
    <GrundlagenKarte
      titel="Koordination — Pflichtbauteile"
      beschreibung="Bauteile, deren Durchdringung nie irrelevant eingestuft werden darf."
    >
      {(d) => <Pflichtbauteile pflichtbauteile={d.pflichtbauteile ?? []} />}
    </GrundlagenKarte>
  );
}

// ─── Berechnung ─────────────────────────────────────────────────────────

/**
 * Bestand und Lücken kommen aus denselben Grundlagen, die Einstufung (`k`)
 * aus einem eigenen, zweiten Aufruf — wie in `Koordination.tsx`, nur ohne
 * dessen übrige, hier nicht benötigte Zustände.
 */
export function KoordinationKollisionenUndEinstufungSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { d, fehler } = useKoordinationGrundlagen(api, projekt.id);
  const [k, setK] = useState<any | null>(null);
  const [kFehler, setKFehler] = useState<string | null>(null);

  useEffect(() => {
    api.koordinationKollisionen(projekt.id)
      .then(setK)
      .catch((e) => setKFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt.id]);

  return (
    <>
      <PageHeader
        titel="Koordination — Kollisionen und Einstufung"
        beschreibung={'Der zu prüfende Bestand und die Einstufung — „echt" muss die '
          + 'kleinste Gruppe bleiben.'}
      />
      {(fehler || kFehler) && (
        <ErrorState
          meldung="Die Kollisionsdaten konnten nicht vollständig geladen werden."
          technisch={fehler ?? kFehler ?? undefined}
        />
      )}
      {!d && !fehler && (
        <LoadingState text="Bestand wird geladen …" zeilen={6} spalten={4} />
      )}
      {d && (
        <KollisionenUndEinstufung bestand={d.bestand} luecken={d.luecken ?? []} k={k} />
      )}
    </>
  );
}

export function KoordinationAuswertungSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const [loesungen, setLoesungen] = useState<any | null>(null);
  const [bericht, setBericht] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ableitetLaeuft, setAbleitetLaeuft] = useState(false);

  const ladeLoesungen = () => {
    api.koordinationLoesungen(projekt.id)
      .then((r) => { setLoesungen(r); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  useEffect(ladeLoesungen, [api, projekt.id]);

  useEffect(() => {
    api.koordinationBericht(projekt.id).then(setBericht).catch(() => setBericht(null));
  }, [api, projekt.id]);

  const onAbleiten = async () => {
    setAbleitetLaeuft(true);

    try {
      await api.koordinationLoesungenAbleiten(projekt.id);
      ladeLoesungen();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setAbleitetLaeuft(false);
    }
  };

  return (
    <>
      <PageHeader
        titel="Koordination — Auswertung"
        beschreibung="Lösungen nach Lösungsart und Gewerkpaar, Automatik-Freigaben und der Bericht."
      />
      {fehler && (
        <ErrorState meldung="Die Auswertung konnte nicht vollständig geladen werden." technisch={fehler} />
      )}
      {!loesungen && !fehler && (
        <LoadingState text="Auswertung wird geladen …" zeilen={6} spalten={4} />
      )}
      {loesungen && (
        <Auswertung
          loesungen={loesungen}
          ableitetLaeuft={ableitetLaeuft}
          onAbleiten={onAbleiten}
          bericht={bericht}
        />
      )}
    </>
  );
}

// ─── Prüfung ────────────────────────────────────────────────────────────

export function KoordinationPlaneraufgabenSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const [planeraufgaben, setPlaneraufgaben] = useState<any | null>(null);
  const [status, setStatus] = useState('');
  const [offenId, setOffenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [entscheidungstext, setEntscheidungstext] = useState('');
  const [erlaeuternLaeuft, setErlaeuternLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = () => {
    api.koordinationPlaneraufgaben(projekt.id, { status: status || undefined })
      .then((r) => { setPlaneraufgaben(r); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  useEffect(laden, [api, projekt.id, status]);

  useEffect(() => {
    if (!offenId) { setDetail(null); return; }

    api.koordinationPlaneraufgabe(projekt.id, offenId)
      .then(setDetail)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt.id, offenId]);

  const onEntscheiden = async (statusNeu: string) => {
    if (!offenId) return;

    try {
      await api.koordinationPlaneraufgabeEntscheiden(
        projekt.id, offenId, { status: statusNeu, entscheidung: entscheidungstext },
      );
      setEntscheidungstext('');
      setOffenId(null);
      laden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  const onErlaeutern = async () => {
    if (!offenId) return;

    setErlaeuternLaeuft(true);

    try {
      await api.koordinationPlaneraufgabeErlaeutern(projekt.id, offenId);
      setDetail(await api.koordinationPlaneraufgabe(projekt.id, offenId));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setErlaeuternLaeuft(false);
    }
  };

  return (
    <>
      <PageHeader
        titel="Koordination — Planeraufgaben"
        beschreibung="Ein Cluster ist eine Entscheidung — Kollisionen im vollen Deckenhohlraum, wo kein Gewerk mehr ausweichen kann."
      />
      {fehler && (
        <ErrorState meldung="Die Planeraufgaben konnten nicht geladen werden." technisch={fehler} />
      )}
      {!planeraufgaben && !fehler && (
        <LoadingState text="Planeraufgaben werden geladen …" zeilen={6} spalten={4} />
      )}
      {planeraufgaben && (
        <Planeraufgaben
          planeraufgaben={planeraufgaben}
          status={status}
          onStatusChange={setStatus}
          offenId={offenId}
          onToggle={(id) => setOffenId(offenId === id ? null : id)}
          detail={detail}
          entscheidungstext={entscheidungstext}
          onEntscheidungstextChange={setEntscheidungstext}
          onEntscheiden={onEntscheiden}
          onErlaeutern={onErlaeutern}
          erlaeuternLaeuft={erlaeuternLaeuft}
        />
      )}
    </>
  );
}

// ─── Daten ──────────────────────────────────────────────────────────────

export function KoordinationAufnahmeUndStandSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { d, fehler } = useKoordinationGrundlagen(api, projekt.id);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [suche, setSuche] = useState<any | null>(null);
  const [sucht, setSucht] = useState(false);

  const quellen = (projekt.modelle ?? []).filter(
    (m) => m.isActive !== false && (m.rollen ?? []).length > 0);

  // Parametertyp bewusst nur die Felder, die `AufnahmeUndStand.tsx` in
  // seinem eigenen (nicht exportierten) `Modell` erwartet - sonst lehnt TS
  // die Zuweisung ab (die dortige Schnittstelle ist schmaler als `Modell`
  // aus `api.ts`).
  const onAufnehmen = async (m: Pick<Modell, 'id' | 'name' | 'revitVersion'>) => {
    setLaeuft(m.id);
    setMeldung(null);

    try {
      const profile = await api.profile(projekt.id, m.revitVersion);
      const profilId = profile.flatMap((x) => x.versions)[0]?.id ?? null;

      if (!profilId) {
        setMeldung(`Kein freigegebenes Exportprofil für Revit `
                 + `${m.revitVersion}. Ohne Profil lässt sich kein Auftrag anlegen.`);
        return;
      }

      const auftrag = await api.auftragAnlegen({
        projectId: projekt.id,
        exportProfileVersionId: profilId,
        modelIds: [m.id],
        kinds: [130], // 130 — die Koordinationsaufnahme.
      });

      setMeldung(`${m.name}: Auftrag ${auftrag.jobNumber} angelegt. Er liest Achse `
               + `und Bounding Box aller Bauteile, dazu die Fremdmodelle als `
               + `Begrenzung.`);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const onSuchen = async () => {
    setSucht(true);
    setSuche(null);

    try {
      setSuche(await api.koordinationSuchen(projekt.id));
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setSucht(false);
    }
  };

  const ohneLauf = (d?.staende ?? []).length === 0;

  return (
    <>
      <PageHeader
        titel="Koordination — Aufnahme und Stand"
        beschreibung="Die Koordinationsaufnahme je Gewerkemodell und die Kollisionssuche über den Rasterindex."
      />
      {fehler && (
        <ErrorState meldung="Der Stand konnte nicht geladen werden." technisch={fehler} />
      )}
      {!d && !fehler && (
        <LoadingState text="Stand wird geladen …" zeilen={4} spalten={3} />
      )}
      {d && (
        <AufnahmeUndStand
          projektId={projekt.id}
          ohneLauf={ohneLauf}
          quellen={quellen}
          laeuft={laeuft}
          onAufnehmen={onAufnehmen}
          meldung={meldung}
          suche={suche}
          sucht={sucht}
          onSuchen={onSuchen}
        />
      )}
    </>
  );
}
